/**
 * This file coordinates the workflow for the main view.
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getCollectionRequest } from "services/apiRequests";
import { useLocation, useNavigate } from "react-router-dom";
import { SchoolListType } from "utils/interfaces";
import { logoutFunction, validateClient } from "services/authRequests";
import { MainContext, RootContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import MainHeader from "components/main-view/utils/main-header";
import SideBar from "components/main-view/utils/side-bar";
import Lessons from "components/main-view/lessons";
import Classes from "components/main-view/classes";
import SubHeaderBar from "components/main-view/utils/sub-header";
import Dashboard from "components/main-view/dashboard";
import InactivityTimer from "components/activity-timer/InactivityTimer";
import DashboardHeaderOptions from "components/main-view/utils/header-options/DashboardHeaderOptions";
import ClassesHeaderOptions from "components/main-view/utils/header-options/ClassesHeaderOptions";
import LessonsHeaderOptions from "components/main-view/utils/header-options/LessonsHeaderOptions";
import SchoolProfile from "components/main-view/utils/school-profile";
import LoadingPanel from "components/utils/LoadingPanel";
import { getUserIdFromToken } from "services/userRequests";

export const MainView: React.FC = () => {
  // Get the platform context
  const { idleTimeout } = usePlatform();

  // States definition
  const [activeItem, setActiveItem] = useState("dashboard");
  const [showSideBarMenu, setShowSideBarMenu] = useState(true);
  // User defined states
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userSchoolId, setUserSchoolId] = useState("");
  const [userSchool, setUserSchool] = useState<SchoolListType | null>(null);
  const [userClassId, setUserClassId] = useState("");
  // Auxiliar state for the edit school profile on customer admin
  const [showSuccesfulSchoolEdit, setShowSuccessfulSchoolEdit] =
    useState(false);
  // State used for master admins school definition
  const [contextSchool, setContextSchool] = useState<SchoolListType | null>(
    null
  );
  // State to control the render of the main view
  const [mainViewReady, setMainViewReady] = useState(false);

  // Navigate hook
  const navigate = useNavigate();

  // References definition
  const dashboardRef: React.RefObject<any> = useRef();
  const lessonsRef: React.RefObject<any> = useRef();
  const classesRef: React.RefObject<any> = useRef();
  const headerRef: React.RefObject<any> = useRef();
  const subHeaderRef: React.RefObject<any> = useRef();
  const schoolProfileRef: React.RefObject<any> = useRef();

  // Define the school for the query based on the user role
  const contextSchoolByRole = ["customer admin", "class admin"].includes(
    userRole
  )
    ? userSchool
    : userRole === "master admin"
    ? contextSchool
    : userSchool;

  // Get a state if we are receiving a state variable from another url
  const { state } = useLocation();

  // Get context values
  const { setFeedbucketUsername, setFeedbucketEmail, setFeedbucketToken } =
    useContext(RootContext);

  /**
   * Check if the user has an access token every time we enter this view
   */
  useEffect(() => {
    validateClient().then(
      (response: { connected: boolean; navigateTo: string }) => {
        // Check if the token is validated
        if (!response.connected) {
          return logoutFunction();
          
        } else {
          // Get the user's profile info
          getCollectionRequest("/api/users/my-profile-info").then(
            (response) => {
              // Check if the response is valid
              if (!(response && response.successful)) {
                return logoutFunction();
              }

              // Get the user
              const user = response.content;

              // And set the states
              setUserFirstName(user.firstName);
              setUserLastName(user.lastName);
              setUserRole(user.role);
              setUserEmail(user.email);
              // Check if the user contains organisations
              if (user.organisations && user.organisations.length > 0) {
                // Set the first one
                setUserSchoolId(user.organisations[0]);
              }
              // Check if the user contains classes
              if (user.classes && user.classes.length > 0) {
                // Set the first one
                setUserClassId(user.classes[0]);
              }
            }
          );

          // And after everything, use the navRegister to comeback
          // to the expected view
          applyNavRegister();
        }
      }
    );
  }, []);

  /**
   * Define the come back behaviour based on an active item if there is
   * a state with an active item defined
   */
  const applyNavRegister = () => {
    if (state) {
      // Set the selected active item
      if (state.activeItem) setActiveItem(state.activeItem);

      // Check first if we have a navRegister defined
      if (state.navRegister && state.navRegister.length > 0) {
        // Wait until lessonsRef is set
        const waitForLessonsRef = new Promise((resolve) => {
          const checkRef = () => {
            if (lessonsRef.current) {
              resolve(true);
            } else {
              requestAnimationFrame(checkRef);
            }
          };
          checkRef();
        });

        // Once the lessons ref is defined, set the navRegister
        waitForLessonsRef.then(() => {
          (lessonsRef.current as any).startFromLastNavRegister(
            state.navRegister
          );
        });
      }
      // And set the lessons list to the selected lesson if available
      else if (state.activeLesson) {
        // Wait until lessonsRef is set
        const waitForLessonsRef = new Promise((resolve) => {
          const checkRef = () => {
            if (lessonsRef.current) {
              resolve(true);
            } else {
              requestAnimationFrame(checkRef);
            }
          };
          checkRef();
        });

        // Once the lessons ref is defined, set the active lesson
        waitForLessonsRef.then(() => {
          (lessonsRef.current as any).setLessonListToShow(state.activeLesson);
        });
      }
    }
  };

  /**
   * Function to get the school data of the user
   * @param filter
   * @returns
   */
  const getSchoolData = async (filter: { [key: string]: any } = {}) => {
    const response = await getCollectionRequest(
      "/api/organisations/users-organisations"
    );

    // Check if the response is successful
    if (!response.successful) {
      return [];
    }

    // Get the data
    const data = response.content;

    // Assign the data to the corresponding element
    setUserSchool(data && data.length > 0 ? data[0] : undefined);
    setContextSchool(data && data.length > 0 ? data[0] : undefined);

    return data;
  };

  /**
   * Get data on loading
   */
  useEffect(() => {
    if (["customer admin", "class admin", "student"].includes(userRole)) {
      getSchoolData({ _id: userSchoolId });
    } else if (userRole === "master admin") {
      getSchoolData();
    }
  }, [userRole, userSchoolId]);

  /**
   * Update school data when showing the successful edit school message
   * on customer admin view
   */
  useEffect(() => {
    if (showSuccesfulSchoolEdit) {
      getSchoolData({ _id: userSchoolId });
    }
  }, [showSuccesfulSchoolEdit]);

  /**
   * Define the state to be ready for show the main view
   */
  useEffect(() => {
    if (
      userRole === "root admin" ||
      userRole === "master admin" ||
      (["customer admin", "class admin", "student"].includes(userRole) &&
        userSchoolId &&
        userSchool)
    ) {
      setMainViewReady(true);
    }
  }, [userSchoolId, userSchool, userRole]);

  /**
   * Define the feedbucket profile
   */
  useEffect(() => {
    if (userFirstName && userLastName) {
      setFeedbucketUsername(`${userFirstName} ${userLastName}`);
      setFeedbucketEmail(userEmail);
      setFeedbucketToken(getUserIdFromToken());
    }
  }, [userFirstName, userLastName, userEmail]);

  /**
   * Function used to render the main content based on the tab selected
   * @returns
   */
  const renderContent = (activeItem: string) => {
    switch (activeItem) {
      case "dashboard":
        return {
          component: (
            <Dashboard
              ref={dashboardRef}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          ),
          headerOptions: (ref: React.RefObject<any>) => (
            <DashboardHeaderOptions ref={ref} dashboardRef={dashboardRef} />
          ),
          onBackButtonClick: () => dashboardRef.current.onBackButtonClick(),
        };
      case "school-profile":
        return {
          component: (
            <SchoolProfile
              ref={schoolProfileRef}
              schoolId={userSchoolId}
              setShowSuccessfulSchoolEdit={setShowSuccessfulSchoolEdit}
              setActiveItem={setActiveItem}
              headerRef={headerRef}
              subHeaderRef={subHeaderRef}
            />
          ),
          headerOptions: (ref: React.RefObject<any>) => (
            <DashboardHeaderOptions ref={ref} dashboardRef={schoolProfileRef} />
          ),
          onBackButtonClick: () => schoolProfileRef.current.onBackButtonClick(),
        };
      case "classes":
        return {
          component: <Classes ref={classesRef} headerRef={headerRef} />,
          headerOptions: (ref: React.RefObject<any>) => (
            <ClassesHeaderOptions ref={ref} classesRef={classesRef} />
          ),
          onBackButtonClick: () => classesRef.current.onBackButtonClick(),
        };
      case "lessons":
        return {
          component: <Lessons ref={lessonsRef} headerRef={headerRef} />,
          headerOptions: (ref: React.RefObject<any>) => (
            <LessonsHeaderOptions ref={ref} lessonsRef={lessonsRef} />
          ),
          onBackButtonClick: () => lessonsRef.current.onBackButtonClick(),
        };
      default:
        return {
          component: <></>,
          headerOptions: (ref: React.RefObject<any>) => <></>,
          onBackButtonClick: () => {},
        };
    }
  };

  /**
   * Function used on idle time out
   */
  const onIdleTimeout = async () => {
    await logoutFunction(navigate);
  };

  // Define the current component to render
  const contentToRender = renderContent(activeItem);

  return (
    <MainContext.Provider
      value={{
        userSchool,
        contextSchool,
        setContextSchool,
        userClassId,
        userRole,
        userFirstName,
        userLastName,
        contextSchoolByRole,
        setShowSideBarMenu,
      }}
    >
      <InactivityTimer timeoutMs={idleTimeout} onTimeout={onIdleTimeout} />
      <AnimatePresence mode="sync">
        {mainViewReady ? (
          <motion.div
            key="main-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="w-screen h-screen min-h-screen p-8 flex flex-col gap-4"
          >
            <MainHeader
              ref={headerRef}
              headerOptions={contentToRender.headerOptions}
              backButtonFunction={contentToRender.onBackButtonClick}
            />
            <SubHeaderBar
              ref={subHeaderRef}
              activeItem={activeItem}
              dashboardRef={dashboardRef}
            />
            <div className="w-full h-full grow shrink justify-start items-start gap-8 inline-flex overflow-y-hidden overflow-x-visible">
              {showSideBarMenu && (
                <SideBar
                  selected={activeItem}
                  setActiveItem={setActiveItem}
                  showSuccesfulSchoolEdit={showSuccesfulSchoolEdit}
                  setShowSuccessfulSchoolEdit={setShowSuccessfulSchoolEdit}
                  headerRef={headerRef}
                />
              )}
              {contentToRender.component}
            </div>
          </motion.div>
        ) : (
          <LoadingPanel key="loading" />
        )}
      </AnimatePresence>
    </MainContext.Provider>
  );
};

export default MainView;
